// Pulled from Creed, not sure we actually need all these settings

import {
  LitElement,
  html,
  classMap,
  css,
  unsafeHTML,
  t,
  paths,
} from '@hh/global.js'

class HHWistiaVideoWrapper extends LitElement {
  static properties = {
    videoId: {
      type: String,
      reflect: true,
      attribute: 'video-id',
    },
    paused: {
      type: Boolean,
      reflect: true,
    },
    muted: {
      type: Boolean,
      reflect: true,
    },
    video: {
      type: Object,
    },
    wistiaControls: {
      type: Boolean,
      reflect: true,
      attribute: 'wistia-controls',
    },
    forceVideoDimensions: {
      type: Boolean,
      attribute: 'force-video-dimensions',
    },
    showToggleButton: {
      type: Boolean,
      reflect: true,
      attribute: 'show-toggle-button',
    },
  }

  constructor() {
    super()
    window._wq = window._wq || []
    this.paused = true
    this.muted = true
    this.isMotionReduced = matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    this.wistiaOptions = {
      videoFoam: true,
      fitStrategy: 'cover',
      endVideoBehavior: 'loop',
      volume: 0,
      autoplay: !this.isMotionReduced,
      controlsVisibleOnLoad: this.wistiaControls,
      playSuspendedOffScreen: false,
      silentAutoplay: true,
      playPauseNotifier: false,
    }
  }

  firstUpdated() {
    this.containerHeight = this.getBoundingClientRect().height
    const videoId = this.videoId

    if (videoId) {
      window._wq.push({
        id: videoId,
        options: this.wistiaOptions,
        onReady: (video) => {
          this.video = video
          this.initVideo()
        },
      })
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('paused')) {
      this.paused ? this.pause() : this.play()
    }
  }

  initVideo() {
    if (!this.video) return

    if (this.forceVideoDimensions) {
      this.video.videoHeight(this.containerHeight, { constrain: true })
    }
    this.video.mute()
    this.videoReady = true
    this.paused = this.isMotionReduced
  }

  onToggleClick(e) {
    e.preventDefault()
    e.stopPropagation()
    this.paused = !this.paused
  }

  play() {
    if (this.video) {
      this.video.play()
      this.paused = false
    }
  }

  pause() {
    if (this.video) {
      this.video.pause()
      this.paused = true
    }
  }

  handleVolumeToggle(e) {
    e.preventDefault()
    e.stopPropagation()
    if (this.video) {
      this.muted ? this.video.unmute() : this.video.mute()
      this.muted = !this.muted
    }
  }

  render() {
    return html`
      <div class="wrapper">
        <slot name="video"></slot>
        <button
          class="${classMap({
            toggle: true,
            ready: this.videoReady,
            'use-wistia-controls': this.wistiaControls,
            'hide-toggle': !this.showToggleButton,
          })}"
          @click=${this.onToggleClick}
          aria-label=${this.paused
            ? t('accessibility.play_video')
            : t('accessibility.pause_video')}
        >
          ${this.paused ? unsafeHTML(paths.play) : unsafeHTML(paths.pause)}
        </button>
        <button
          class="${classMap({
            toggle: true,
            mute: true,
            ready: this.videoReady,
            'use-wistia-controls': this.wistiaControls,
            '_hide-toggle': !this.showToggleButton,
            get 'hide-toggle'() {
              return this['_hide-toggle']
            },
            set 'hide-toggle'(value) {
              this['_hide-toggle'] = value
            },
          })}"
          @click="${this.handleVolumeToggle}"
          aria-label=${this.muted
            ? t('accessibility.sound_video')
            : t('accessibility.mute_video')}
        >
          ${this.muted ? unsafeHTML(paths.mute) : unsafeHTML(paths.sound)}
        </button>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    :host {
      display: block;
      height: 100%;
      background: var(--hh-color-foreground);
    }

    .wrapper {
      height: 100%;
      position: relative;
    }

    .toggle {
      display: none;
      position: absolute;
      bottom: 2rem;
      right: 1rem;
      width: 4rem;
      height: 4rem;
      border-radius: 50%;
      border: none;
      color: var(--hh-color-background);
      background-color: var(--hh-color-day-extra-light-opacity);
      cursor: pointer;
      padding: 0;
      fill: currentColor;
      align-items: center;
      justify-content: center;
      z-index: 11;
    }

    .toggle.mute {
      right: 5rem;
    }

    @media screen and (min-width: 750px) {
      .toggle {
        right: 2rem;
      }

      .toggle.mute {
        right: 6rem;
      }
    }

    .toggle.hide-toggle {
      display: none !important;
    }

    .toggle.use-wistia-controls {
      display: none;
    }

    .toggle.ready {
      display: flex;
    }

    .toggle:not(.mute) svg {
      fill: transparent;
      transition: fill var(--hh-duration-default) ease-in-out;
    }

    @media (hover: hover) {
      .toggle:is(:focus-visible, :hover):not(.mute) svg {
        fill: currentColor;
      }
    }
  `
}

customElements.define('hh-wistia-video-wrapper', HHWistiaVideoWrapper)
